import { useCallback, useContext, useEffect, useState } from 'react'

import { Loader, Text } from '@cash-me/react-components'
import NextImage from 'next/image'
import Link from 'next/link'

import { billsClient } from 'src/adapters/http'
import { pushDataLayer } from 'utils/push-data-layer'

import { DuplicateBillContext } from '..'
import type { DuplicateBillContextProps, InfoBoxProps } from '../types'

import * as S from './styles'

const ICONS_PATH = `${process.env.NEXT_PUBLIC_SITE_IMAGES_PREFIX}/images/site/duplicate-bill/icon`

const InfoBoxTypes = {
  success: {
    title: 'Seu boleto foi gerado com sucesso!',
    content: (downloadPDF = () => {}) => (
      <a onClick={downloadPDF}>
        <S.ImageWrapper>
          <NextImage
            src={`${ICONS_PATH}/paper.svg`}
            alt="Balão de chat"
            width="80"
            height="80"
            sizes="100vw"
            style={{
              width: '100%',
              height: 'auto'
            }}
          />
        </S.ImageWrapper>
        <Text size="heading5" weight="medium" color="var(--colors-blue-text-default)">
          Baixar boleto
        </Text>
      </a>
    ),
    dataLayerLabel: 'Informativo Boleto gerado com sucesso'
  },
  error: {
    title: 'Aviso',
    content: () => (
      <>
        <S.ImageWrapper>
          <NextImage
            src={`${ICONS_PATH}/message.svg`}
            alt="Balão de chat"
            width="80"
            height="80"
            sizes="100vw"
            style={{
              width: '100%',
              height: 'auto'
            }}
          />
        </S.ImageWrapper>
        <Text size="body1" weight="medium" color="var(--colors-neutral-text-default)">
          Não foi possível gerar o seu boleto por aqui, entre em contato com a nossa equipe pelo telefone{' '}
          <b>(11) 4040-2550 opção 1</b> ou e-mail{' '}
          <Link href="mailto:suporte.pagamento@cashme.com.br">suporte.pagamento@cashme.com.br</Link>
        </Text>
      </>
    ),
    dataLayerLabel: 'Informativo Não foi possível gerar boleto'
  },
  'no-info': {
    title: 'Não sei o número do contrato',
    content: () => (
      <>
        <S.ImageWrapper>
          <NextImage
            src={`${ICONS_PATH}/message.svg`}
            alt="Balão de chat"
            width="80"
            height="80"
            sizes="100vw"
            style={{
              width: '100%',
              height: 'auto'
            }}
          />
        </S.ImageWrapper>
        <Text size="body1" weight="medium" color="var(--colors-neutral-text-default)">
          Entre em contato com a nossa equipe pelo telefone <b>(11) 4040-2550</b> ou e-mail{' '}
          <Link href="mailto:suporte.pagamento@cashme.com.br">suporte.pagamento@cashme.com.br</Link>
        </Text>
      </>
    )
  },
  'not-my-info': {
    title: 'Não é meu e-mail ou número de celular',
    content: () => (
      <>
        <S.ImageWrapper>
          <NextImage
            src={`${ICONS_PATH}/message.svg`}
            alt="Balão de chat"
            width="80"
            height="80"
            sizes="100vw"
            style={{
              width: '100%',
              height: 'auto'
            }}
          />
        </S.ImageWrapper>
        <Text size="body1" weight="medium" color="var(--colors-neutral-text-default)">
          Para atualizar suas informações entre em contato com a nossa equipe pelo telefone <b>(11) 4040-2550</b> ou
          e-mail <Link href="mailto:suporte.pagamento@cashme.com.br">suporte.pagamento@cashme.com.br</Link>
        </Text>
      </>
    ),
    dataLayerLabel: 'Informativo Não reconheço meus dados cadastrais'
  },
  'wrong-info': {
    title: 'Aviso',
    content: () => (
      <>
        <S.ImageWrapper>
          <NextImage
            src={`${ICONS_PATH}/message.svg`}
            alt="Balão de chat"
            width="80"
            height="80"
            sizes="100vw"
            style={{
              width: '100%',
              height: 'auto'
            }}
          />
        </S.ImageWrapper>
        <Text size="body1" weight="medium" color="var(--colors-neutral-text-default)">
          Não encontramos o seu CPF/CNPJ ou número do contrato, verifique se estão corretos ou entre em contato com a
          nossa equipe pelo telefone <b>(11) 4040-2550</b> ou e-mail{' '}
          <Link href="mailto:suporte.pagamento@cashme.com.br">suporte.pagamento@cashme.com.br</Link>
        </Text>
      </>
    ),
    dataLayerLabel: 'Informativo Informações incorretas'
  },
  'no-bill': {
    title: 'Aviso',
    content: () => (
      <>
        <S.ImageWrapper>
          <NextImage
            src={`${ICONS_PATH}/message.svg`}
            alt="Balão de chat"
            width="80"
            height="80"
            sizes="100vw"
            style={{
              width: '100%',
              height: 'auto'
            }}
          />
        </S.ImageWrapper>
        <Text size="body1" weight="medium" color="var(--colors-neutral-text-default)">
          Não encontramos boleto disponível, em caso de dúvidas entre em contato com a nossa equipe pelo telefone{' '}
          <b>(11) 4040-2550</b> ou e-mail{' '}
          <Link href="mailto:suporte.pagamento@cashme.com.br">suporte.pagamento@cashme.com.br</Link>
        </Text>
      </>
    ),
    dataLayerLabel: 'Informativo Sem boleto disponível'
  },
  'server-error': {
    title: 'Instabilidade no servidor',
    content: () => (
      <>
        <S.ImageWrapper>
          <NextImage
            src={`${ICONS_PATH}/message.svg`}
            alt="Balão de chat"
            width="80"
            height="80"
            sizes="100vw"
            style={{
              width: '100%',
              height: 'auto'
            }}
          />
        </S.ImageWrapper>
        <Text size="body1" weight="medium" color="var(--colors-neutral-text-default)">
          Estamos tendo problemas com o nosso servidor. Ele deve voltar em breve mas caso precise, pode entrar em
          contato com a nossa equipe pelo telefone <b>(11) 4040-2550</b> ou e-mail{' '}
          <Link href="mailto:suporte.pagamento@cashme.com.br">suporte.pagamento@cashme.com.br</Link>
        </Text>
      </>
    )
  }
}

export const InfoBox = (props?: InfoBoxProps) => {
  const { fields, routeCallback } = useContext<DuplicateBillContextProps>(DuplicateBillContext)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [content, setContent] = useState(InfoBoxTypes[props?.type ?? 'server-error'])

  const getPDF = useCallback(async () => {
    setIsLoading(true)

    try {
      if (!fields.contract || !fields.pdfId) throw new Error('Campos não preenchidos.')
      const { status, data } = await billsClient.getPDF(fields.contract, fields.pdfId)

      if (status === 200) {
        const anchor = document.createElement('a')
        anchor.href = `data:application/pdf;base64,${data}`
        anchor.download = 'CashMe - boleto.pdf'
        anchor.click()
      }
    } catch (e) {
      const status = (e as { response: { status: number } }).response?.status
      if (status >= 500) {
        setContent(InfoBoxTypes['server-error'])
      } else {
        setContent(InfoBoxTypes['error'])
      }
    } finally {
      setIsLoading(false)
    }
  }, [fields.contract, fields.pdfId])

  useEffect(() => {
    if (content?.dataLayerLabel)
      pushDataLayer({
        category: 'Visualização',
        action: 'Sou cliente',
        label: content.dataLayerLabel
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <S.InfoBoxWrapper>
      <Text size="heading5" weight="medium" color="var(--colors-neutral-text-default)">
        {content.title}
      </Text>
      <S.BlueBox>
        {!isLoading ? (
          props?.type === 'success' ? (
            content.content(getPDF)
          ) : (
            content.content(getPDF)
          )
        ) : (
          <S.LoaderWrapper>
            <Loader size="lg" />
          </S.LoaderWrapper>
        )}
      </S.BlueBox>
      <S.BackButtonWrapper>
        <S.Link onClick={() => routeCallback('form', {})}>
          <Text size="body1" weight="medium" color="var(--colors-blue-text-default)">
            Voltar
          </Text>
        </S.Link>
      </S.BackButtonWrapper>
    </S.InfoBoxWrapper>
  )
}
